<template>
  <template v-if="component">
    <Breadcrumbs :data="showBreadcrumbs" />
    <component
      :is="showComponent"
      :data="context"
      :page-data="pageData"
      :seo-data="seoData"
      seo-text-show
      @change-data="(e) => changePageData(e)"
      @get-breadcrumbs="getBreadcrumbs"
      >Dynamic page {{ component }}
    </component>
  </template>
</template>

<script lang="ts" setup>
import { useGlobalStore } from '~/stores/global';
import type { PageDataInterface } from '~/interfaces/api';
import { getCategories } from '~/api/catalogCategory';
import type { BreadcrumbsItem, BreadcrumbsData } from '~/interfaces/page-info';
import { getNav } from '~/components/distributor/initModelNew';
import { base64ToObj } from '~/utils/base64';

const globalStore = useGlobalStore();
const pageData = ref<PageDataInterface>(
  base64ToObj(globalStore.pageDistributorData).data
);
const seoData = ref(globalStore.seoData);
const keyIndex = +new Date();
const showComponent = shallowRef('div');
const showBreadcrumbs = ref<BreadcrumbsItem[]>(
  seoData.value?.page?.breadcrumbs
);
const context = computed(() => ({
  ...seoData.value.page.context,
  blocks: seoData.value?.page?.blocks || [],
  h1: seoData.value?.page?.h1 || '',
  text: seoData.value?.page?.text || '',
  title: seoData.value?.page?.title || '',
  description: seoData.value?.page?.description || '',
  component: seoData.value?.page?.component,
}));
const component = seoData.value?.page?.component;

switch (component) {
  case 'model':
    showComponent.value = defineAsyncComponent(
      () => import('~/components/page-model/PageModel.vue')
    );
    break;
  case 'brand': // страница товаров бренда
    showComponent.value = defineAsyncComponent(
      () => import('~/components/brand/brand.vue')
    );
    break;
  case 'category-list': // страница списка категорий
  case 'category-brand': // страница категорий бренда
  case 'category-preset': // страница с предустановками, например: "китайские телефоны"
  case 'category-discount': // страница со скидками
    showComponent.value = defineAsyncComponent(
      () => import('~/components/category-list/CategoryList.vue')
    );
    break;
  case 'promocodes-category': // промокоды в категории
    showComponent.value = defineAsyncComponent(
      () => import('~/components/promocodes-category/PromocodesCategory.vue')
    );
    break;
  case 'catalog-category': // каталог категорий
    showComponent.value = defineAsyncComponent(
      () => import('~/components/catalog-category/CatalogCategory.vue')
    );
    break;
  case 'shop-offers': // предложение магазина
    showComponent.value = defineAsyncComponent(
      () => import('~/components/shop-offers/ShopOffers.vue')
    );
    break;
  case 'shop-promocodes': // промокоды магазина
    showComponent.value = defineAsyncComponent(
      () => import('~/components/shop-promocodes/ShopPromocodes.vue')
    );
    break;
}

function changePageData({ key, data }: { key: string; data: string }) {
  if (pageData.value) {
    pageData.value[key] = data;
    if (key === 'active' || (component === 'model' && key === 'voprosy')) {
      pageData.value.changePage = !(
        (pageData.value?.active !== data &&
          (pageData.value?.active === 'otzyvy' ||
            data === 'otzyvy' ||
            pageData.value?.active === 'voprosy')) ||
        data === 'voprosy'
      );
    }
    if (component === 'model') {
      pageData.value.navSections = getNav(pageData.value);
    }
  }
}

const setBreadcrumbs = (data: BreadcrumbsData): BreadcrumbsItem[] => {
  if (!data.parents || !data.parents.length)
    return [
      {
        slug: '',
        title: 'Главная',
      },
      {
        slug: data.slug,
        title: data?.name,
      },
    ];
  else
    return [
      ...setBreadcrumbs(data.parents[0]),
      {
        slug: data.slug,
        title: data?.name,
      },
    ];
};

async function getBreadcrumbs(id: number) {
  const newBreadcrumbs = await getCategories(String(id), {
    params: {
      region_id: globalStore.region.id,
      device: globalStore.device || 1,
      parents: true,
      ...(globalStore.adult ? { adult: globalStore.adult } : {}),
    },
  });
  if (newBreadcrumbs) {
    showBreadcrumbs.value = setBreadcrumbs(newBreadcrumbs);
  }
}

onBeforeRouteUpdate((to, from, next) => {
  if (!import.meta.env.SSR) globalStore.setBackUrl(from.fullPath);
  if (globalStore.isOpenAdultModal) globalStore.setOpenAdultModal(false);
  next();
});
onBeforeRouteLeave((to, from, next) => {
  if (globalStore.isOpenAdultModal)
    setTimeout(() => globalStore.setOpenAdultModal(false), 1000);
  next();
});
onMounted(() => {
  globalStore.clearCancelToken();
});
</script>
